// src/pages/Dashboard.tsx
import React, { useEffect, useState, useRef } from "react";
import "../css/dashboard.css";
import { useAuth } from "../security/AuthLogin";
import { Line } from "react-chartjs-2";
import "react-calendar/dist/Calendar.css";
import Calendar from "react-calendar";
import MapChart from "../components/MapChart";
import { getMapaProvincias, MapaProvincia } from "../api/dashboardApi";
import CircularProgress from "../components/CircularProgress";
// Importaciones adicionales en Dashboard.tsx
import Noticias from "../components/Noticias";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import {
  Period,
  getConcursosPresentados,
  getAdjudicacionesPresentadas,
  getFavoritosViablesNoPresentado,
  getConcursosEnPlazo,
  getPresentadosVsTotalAdjudicaciones,
  getFavoritosUrgentes,
  FavoritoUrgente,
  DashboardChartResponse,
  getCalendarioFechas,
  getCalendarioDetalle,
  CalendarioDetalle,
  getFacturacionAnual,
  FacturacionAnualResponse,
  getMediaNumeroLicitadores,
} from "../api/dashboardApi";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Hook para detectar tamaño de pantalla
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return { isMobile, isTablet };
};

// Opciones de gráfico mejoradas para responsive
const getChartOptions = (isMobile: boolean) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(30, 41, 59, 0.9)",
      titleFont: {
        family: "Inter, sans-serif",
        size: isMobile ? 12 : 14,
        weight: "bold" as const,
      },
      bodyFont: { family: "Inter, sans-serif", size: isMobile ? 11 : 13 },
      padding: isMobile ? 8 : 12,
      cornerRadius: 8,
      displayColors: true,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { family: "Inter, sans-serif", size: isMobile ? 10 : 12 },
        maxRotation: isMobile ? 45 : 0,
        minRotation: 0,
      },
    },
    y: {
      min: 0,
      suggestedMax: 5,
      ticks: {
        stepSize: 1,
        precision: 0,
        font: { family: "Inter, sans-serif", size: isMobile ? 10 : 12 },
      },
      grid: { color: "rgba(0, 0, 0, 0.05)" },
    },
  },
  interaction: {
    intersect: false,
    mode: "index" as const,
  },
  elements: {
    point: {
      radius: isMobile ? 2 : 3,
      hoverRadius: isMobile ? 4 : 6,
    },
    line: {
      tension: 0.4,
    },
  },
});

type DashboardProps = {
  role: "admin" | "cliente" | "tecnico";
};

const MONTH_NAMES_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function Dashboard({ role }: DashboardProps) {
  const { userName, userId: id_usuario } = useAuth();
  const { isMobile, isTablet } = useResponsive();

  // Estados para KPIs y métricas
  const [selectedPeriodConc, setSelectedPeriodConc] = useState<Period>("total");
  const [selectedPeriodAdju, setSelectedPeriodAdju] = useState<Period>("total");
  const [facturacion, setFacturacion] = useState<FacturacionAnualResponse>({
    success: false,
    labels: [],
    data: [],
    prevTotal: 0,
    prevYear: new Date().getFullYear() - 1,
  });
  const [mapaData, setMapaData] = useState<MapaProvincia[]>([]);
  const [mediaNumeroLicitadores, setMediaNumeroLicitadores] = useState(0);
  const [concursosChart, setConcursosChart] = useState<DashboardChartResponse>({
    success: false,
    labels: [],
    data: [],
  });
  const [adjudicacionesChart, setAdjudicacionesChart] =
    useState<DashboardChartResponse>({
      success: false,
      labels: [],
      data: [],
    });
  const [favoritosViablesNoPresentado, setFavoritosViablesNoPresentado] =
    useState(0);
  const [concursosEnPlazo, setConcursosEnPlazo] = useState(0);
  const [adjudicacionesGanadas, setAdjudicacionesGanadas] = useState(0);
  const [adjudicacionesPresentadas, setAdjudicacionesPresentadas] = useState(0);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  // Estados para calendario
  const [markedDates, setMarkedDates] = useState<Set<string>>(new Set());
  const [dayInfo, setDayInfo] = useState<{
    fecha: string;
    concursos: CalendarioDetalle[];
    anchor: { x: number; y: number } | null;
  }>({ fecha: "", concursos: [], anchor: null });

  // Estados para favoritos urgentes (admin/técnico)
  const [favoritosGrouped, setFavoritosGrouped] = useState<{
    [userId: string]: FavoritoUrgente[];
  }>({});
  const [expandedUsers, setExpandedUsers] = useState<{
    [userId: string]: boolean;
  }>({});
  const [favObservations, setFavObservations] = useState<{
    [key: string]: string;
  }>({});

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredFavoritos, setFilteredFavoritos] = useState<{
    [userId: string]: FavoritoUrgente[];
  }>({});

  // Función para filtrar favoritos
  const filterFavoritos = (
    favoritos: { [userId: string]: FavoritoUrgente[] },
    term: string
  ) => {
    if (!term.trim()) {
      return favoritos;
    }

    const searchLower = term.toLowerCase().trim();
    const filtered: { [userId: string]: FavoritoUrgente[] } = {};

    Object.entries(favoritos).forEach(([userId, favs]) => {
      const filteredFavs = favs.filter((fav) => {
        // Buscar en nombre del cliente
        const clienteMatch = fav.cliente_nombre
          ?.toLowerCase()
          .includes(searchLower);

        // Buscar en código de expediente
        const expedienteMatch = fav.codigo_expediente
          ?.toLowerCase()
          .includes(searchLower);

        // Buscar en empresa
        const empresaMatch = fav.empresa?.toLowerCase().includes(searchLower);

        // Buscar en título del concurso
        const tituloMatch = fav.titulo_concurso
          ?.toLowerCase()
          .includes(searchLower);

        return clienteMatch || expedienteMatch || empresaMatch || tituloMatch;
      });

      if (filteredFavs.length > 0) {
        filtered[userId] = filteredFavs;
      }
    });

    return filtered;
  };

  // Funciones auxiliares
  function toggleUser(userId: string) {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  }

  function getSoporteName(tipo: number) {
    return (
      [
        "Baja estadística",
        "Soporte técnico",
        "Soporte administrativo",
        "Soporte jurídico",
      ][tipo] ?? "Soporte desconocido"
    );
  }

  function getSoporteUrlPrefix(tipo: number) {
    return (
      [
        "/baja-estadistica/",
        "/soporte-tecnico/",
        "/soporte-administrativo/",
        "/soporte-juridico/",
      ][tipo] ?? "/"
    );
  }

  function getDaysRemainingClass(dias: number) {
    if (dias <= 2) return "dashboard__days-urgent";
    if (dias <= 5) return "dashboard__days-warning";
    return "";
  }

  const fmt = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  async function openTooltipFor(fecha: string, ev?: React.MouseEvent) {
    try {
      const data = await getCalendarioDetalle(fecha);

      // Calcular posición del tooltip evitando que se salga de la pantalla
      let tooltipX = ev ? ev.clientX : window.innerWidth / 2;
      let tooltipY = ev ? ev.clientY : 200;

      // Mejorado: Mejor cálculo para móviles
      if (isMobile) {
        tooltipX = 16; // Margen fijo en móviles
        tooltipY = Math.max(tooltipY - 100, 100); // Evitar que salga por arriba
      } else {
        const tooltipWidth = 400;
        if (tooltipX + tooltipWidth > window.innerWidth) {
          tooltipX = window.innerWidth - tooltipWidth - 20;
        }

        const tooltipHeight = 200;
        if (tooltipY + tooltipHeight > window.innerHeight) {
          tooltipY = tooltipY - tooltipHeight - 20;
        }
      }

      setDayInfo({
        fecha,
        concursos: data,
        anchor: { x: tooltipX, y: tooltipY },
      });
    } catch (err) {
      console.error("Error al obtener detalle de calendario:", err);
    }
  }

  // Cargar datos para cliente
  useEffect(() => {
    if (role !== "cliente") return;

    const loadClientData = async () => {
      setLoadingMetrics(true);
      try {
        const [
          concursosData,
          adjudicacionesData,
          mapaDataResult,
          facturacionData,
          favoritosViables,
          concursosEnPlazoData,
          adjudicacionesRatio,
          mediaLicitadores,
        ] = await Promise.all([
          getConcursosPresentados(selectedPeriodConc),
          getAdjudicacionesPresentadas(selectedPeriodAdju),
          getMapaProvincias(),
          getFacturacionAnual(),
          getFavoritosViablesNoPresentado(),
          getConcursosEnPlazo(),
          getPresentadosVsTotalAdjudicaciones(),
          getMediaNumeroLicitadores(),
        ]);

        setConcursosChart(concursosData);
        setAdjudicacionesChart(adjudicacionesData);
        setMapaData(mapaDataResult);
        setFacturacion(facturacionData);
        setFavoritosViablesNoPresentado(favoritosViables);
        setConcursosEnPlazo(concursosEnPlazoData);
        setAdjudicacionesGanadas(adjudicacionesRatio.ganadas);
        setAdjudicacionesPresentadas(adjudicacionesRatio.presentados);
        setMediaNumeroLicitadores(mediaLicitadores);
      } catch (error) {
        console.error("Error cargando datos del cliente:", error);
        toast.error("Error al cargar los datos del dashboard");
      } finally {
        setLoadingMetrics(false);
      }
    };

    loadClientData();
  }, [role, selectedPeriodConc, selectedPeriodAdju]);

  // Cargar fechas del calendario para cliente
  useEffect(() => {
    if (role !== "cliente") return;

    const loadCalendarData = async () => {
      try {
        const fechas = await getCalendarioFechas();
        setMarkedDates(new Set(fechas));
      } catch (err) {
        console.error("Error getCalendarioFechas:", err);
      }
    };

    loadCalendarData();
  }, [role]);

  // Cargar favoritos urgentes para admin/técnico
  useEffect(() => {
    if (role !== "admin" && role !== "tecnico") return;

    const loadFavoritosUrgentes = async () => {
      try {
        const favs = await getFavoritosUrgentes();
        const grouped = favs.reduce((acc, item) => {
          (acc[item.id_usuario] ??= []).push(item);
          return acc;
        }, {} as { [userId: string]: FavoritoUrgente[] });
        setFavoritosGrouped(grouped);
      } catch (err) {
        console.error("Error al obtener favoritos urgentes:", err);
        setFavoritosGrouped({});
      }
    };

    loadFavoritosUrgentes();
  }, [role]);

  useEffect(() => {
    const filtered = filterFavoritos(favoritosGrouped, searchTerm);
    setFilteredFavoritos(filtered);
  }, [favoritosGrouped, searchTerm]);

  // Función para guardar observaciones
  const saveObservations = async (
    id_usuario: string,
    id_concurso: string,
    key: string
  ) => {
    const observaciones = favObservations[key] ?? "";
    const token = localStorage.getItem("authToken") ?? "";
    if (!token) {
      window.location.href = "/login";
      return;
    }
    const API_URL = `${window.location.protocol}//${window.location.hostname}/api`;

    try {
      const res = await fetch(
        `${API_URL}/dashboard/admtecnico/favorito-observaciones`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id_usuario, id_concurso, observaciones }),
        }
      );
      const data = await res.json();
      if (data.success) {
        toast.success("¡Observaciones guardadas correctamente!");
      } else {
        toast.error("Error al guardar observaciones.");
      }
    } catch (error) {
      console.error("Error al actualizar observaciones:", error);
      toast.error("Error al guardar observaciones.");
    }
  };

  // Preparar datos para gráficos
  const concursosLabels = concursosChart.labels.map((label) =>
    selectedPeriodConc === "annual"
      ? MONTH_NAMES_ES[Number(label) - 1] ?? label
      : label
  );
  const adjudicacionesLabels = adjudicacionesChart.labels.map((label) =>
    selectedPeriodAdju === "annual"
      ? MONTH_NAMES_ES[Number(label) - 1] ?? label
      : label
  );

  const chartDataConcursos = {
    labels: concursosLabels,
    datasets: [
      {
        label: "Concursos Presentados",
        data: concursosChart.data,
        borderColor: "#b90029",
        backgroundColor: "rgba(185,0,41,0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: isMobile ? 2 : 3,
      },
    ],
  };

  const chartDataAdjudicaciones = {
    labels: adjudicacionesLabels,
    datasets: [
      {
        label: "Adjudicaciones Ganadas",
        data: adjudicacionesChart.data,
        borderColor: "#b90029",
        backgroundColor: "rgba(185,0,41,0.2)",
        fill: true,
        tension: 0.4,
        pointRadius: isMobile ? 2 : 3,
      },
    ],
  };

  // Calcular crecimiento de facturación
  const currentTotal = facturacion.data.reduce((sum, val) => sum + val, 0);
  const growthPercentage =
    facturacion.prevTotal > 0
      ? Math.round(
          ((currentTotal - facturacion.prevTotal) / facturacion.prevTotal) * 100
        )
      : 0;

  return (
    <div className="dashboard">
      {/* Sección CLIENTE - Layout Modernizado */}
      {role === "cliente" && (
        <div className="dashboard__modern-layout">
          {/* Contenido Principal (Izquierda) */}
          <div className="dashboard__main-content">
            {/* FILA 1 - KPI Cards */}
            <div className="dashboard__kpi-row">
              {/* KPI 1 - Concursos en plazo */}
              <div className="dashboard__kpi-card dashboard__kpi-card--blue">
                <div className="dashboard__kpi-icon">
                  <svg
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 11H7v8h2v-8zm4-4h-2v12h2V7zm4-3h-2v15h2V4z" />
                  </svg>
                </div>
                <div className="dashboard__kpi-content">
                  <div className="dashboard__kpi-value">
                    {loadingMetrics ? (
                      <div className="my-spinner" />
                    ) : (
                      concursosEnPlazo.toLocaleString()
                    )}
                  </div>
                  <div className="dashboard__kpi-label">Concursos en plazo</div>
                </div>
              </div>

              {/* KPI 2 - Favoritos Viables */}
              <div className="dashboard__kpi-card dashboard__kpi-card--red">
                <div className="dashboard__kpi-icon">
                  <svg
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
                  </svg>
                </div>
                <div className="dashboard__kpi-content">
                  <div className="dashboard__kpi-value">
                    {loadingMetrics ? (
                      <div className="my-spinner" />
                    ) : (
                      favoritosViablesNoPresentado.toLocaleString()
                    )}
                  </div>
                  <div className="dashboard__kpi-label">
                    Favoritos Viables
                    <br />
                    (sin presentar)
                  </div>
                </div>
              </div>

              {/* KPI 3 - Media Licitadores */}
              <div className="dashboard__kpi-card dashboard__kpi-card--green">
                <div className="dashboard__kpi-icon">
                  <svg
                    width="32"
                    height="32"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                  </svg>
                </div>
                <div className="dashboard__kpi-content">
                  <div className="dashboard__kpi-value">
                    {loadingMetrics ? (
                      <div className="my-spinner" />
                    ) : (
                      mediaNumeroLicitadores.toFixed(1)
                    )}
                  </div>
                  <div className="dashboard__kpi-label">
                    MEDIA Nº LICITADORES
                  </div>
                </div>
              </div>
            </div>

            {/* FILA 2 - Gráfico de Facturación Principal */}
            <div className="dashboard__chart-main">
              <div className="dashboard__chart-header">
                <h3>
                  FACTURACIÓN {new Date().getFullYear()} (
                  {currentTotal.toLocaleString()}€) VS {facturacion.prevYear} (
                  {facturacion.prevTotal.toLocaleString()}€)
                </h3>
                <div
                  className={`dashboard__chart-growth ${
                    growthPercentage >= 0 ? "positive" : "negative"
                  }`}
                >
                  {growthPercentage >= 0 ? "+" : ""}
                  {growthPercentage}%
                </div>
              </div>
              {facturacion.success && (
                <div className="dashboard__chart-container">
                  <Line
                    data={{
                      labels: facturacion.labels.map(
                        (m) =>
                          MONTH_NAMES_ES[m - 1]?.substring(
                            0,
                            isMobile ? 3 : 5
                          ) || `Mes ${m}`
                      ),
                      datasets: [
                        {
                          label: "Importe adjudicado (€)",
                          data: facturacion.data,
                          borderColor: "#dc2626",
                          backgroundColor: "rgba(220,38,38,0.1)",
                          fill: true,
                          tension: 0.4,
                          pointRadius: isMobile ? 2 : 4,
                          pointBackgroundColor: "#dc2626",
                          pointBorderColor: "#fff",
                          pointBorderWidth: 2,
                        },
                      ],
                    }}
                    options={{
                      ...getChartOptions(isMobile),
                      scales: {
                        ...getChartOptions(isMobile).scales,
                        y: {
                          ...getChartOptions(isMobile).scales.y,
                          display: false,
                        },
                        x: {
                          grid: { display: false },
                          ticks: {
                            font: {
                              family: "Inter, sans-serif",
                              size: isMobile ? 10 : 12,
                              weight: 500,
                            },
                            color: "#64748b",
                            maxRotation: isMobile ? 45 : 0,
                            minRotation: 0,
                          },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          ...getChartOptions(isMobile).plugins.tooltip,
                          callbacks: {
                            label: (context: any) =>
                              `${Number(context.parsed.y).toLocaleString(
                                "es-ES"
                              )}€`,
                          },
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>

            {/* FILA 3 - Gráficos Secundarios */}
            <div className="dashboard__secondary-charts">
              {/* Mapa */}
              <div className="dashboard__chart-secondary">
                <div className="dashboard__chart-secondary-header">
                  <h4>Distribución Geográfica</h4>
                </div>
                <div className="dashboard__chart-secondary-content">
                  {mapaData.length > 0 && <MapChart data={mapaData} />}
                </div>
              </div>

              {/* Gráfico Circular */}
              <div className="dashboard__chart-secondary">
                <div className="dashboard__chart-secondary-header">
                  <h4>% ADJUDICACIONES PRESENTADAS / GANADAS</h4>
                  <div className="dashboard__legend">
                    <div className="dashboard__legend-item">
                      <span className="dashboard__legend-dot dashboard__legend-dot--blue"></span>
                      <span>Presentadas: {adjudicacionesPresentadas}</span>
                    </div>
                    <div className="dashboard__legend-item">
                      <span className="dashboard__legend-dot dashboard__legend-dot--purple"></span>
                      <span>Ganadas: {adjudicacionesGanadas}</span>
                    </div>
                  </div>
                </div>
                <div className="dashboard__chart-secondary-content dashboard__circular-center">
                  <CircularProgress
                    percentage={
                      adjudicacionesPresentadas > 0
                        ? Math.round(
                            (adjudicacionesGanadas /
                              adjudicacionesPresentadas) *
                              100
                          )
                        : 0
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Barra Lateral (Derecha) */}
          <div className="dashboard__sidebar">
            {/* Calendario */}
            <div className="dashboard__sidebar-section">
              <div className="dashboard__sidebar-header">
                <h3>Calendario de Favoritos</h3>
              </div>
              <div className="dashboard__calendar-wrapper">
                <Calendar
                  showNeighboringMonth={false}
                  tileClassName={({ date }) =>
                    markedDates.has(fmt(date)) ? "calendar__marked" : undefined
                  }
                  onClickDay={(value, ev) =>
                    openTooltipFor(fmt(value as Date), ev as any)
                  }
                />
              </div>
            </div>

            {/* Panel de Noticias */}
            <div className="dashboard__sidebar-section dashboard__sidebar-section--full-height">
              <div className="dashboard__sidebar-header">
                <h3>PANEL DE NOTICIAS</h3>
              </div>
              <Noticias />
            </div>
          </div>
        </div>
      )}
      {/* Tooltip del calendario */}
      {dayInfo.anchor && (
        <div
          className="calendar-tooltip"
          style={{
            top: isMobile
              ? Math.max(dayInfo.anchor.y - 100, 100)
              : dayInfo.anchor.y + 10,
            left: isMobile ? "1rem" : dayInfo.anchor.x + 10,
            right: isMobile ? "1rem" : "auto",
            maxWidth: isMobile ? "calc(100vw - 2rem)" : "400px",
          }}
          onMouseLeave={() =>
            setDayInfo({ fecha: "", concursos: [], anchor: null })
          }
        >
          {/* ───── Botón de cierre ───── */}
          <button
            className="tooltip-close"
            aria-label="Cerrar"
            onClick={() =>
              setDayInfo({ fecha: "", concursos: [], anchor: null })
            }
          >
            ×
          </button>

          <h4>{dayInfo.fecha}</h4>
          {dayInfo.concursos.length === 0 ? (
            <div>No hay concursos</div>
          ) : (
            dayInfo.concursos.map((c) => (
              <div key={c.id_concurso} className="tooltip-item">
                <div>
                  Expediente: <strong>{c.codigo_expediente}</strong>
                </div>
                <div>
                  Importe:{" "}
                  {c.importe !== null
                    ? Number(c.importe).toLocaleString("es-ES") + "€"
                    : "—"}
                </div>
                <div className="tooltip-buttons">
                  <a
                    className="btn-see"
                    href={`/concurso/${c.id_concurso}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ver concurso
                  </a>
                  <br />
                  <a
                    className="btn-see btn-see--favorito"
                    href={`/favorito/${c.id_concurso}/${id_usuario}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ver favorito
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {/* Sección ADMIN / TECNICO */}
      {(role === "admin" || role === "tecnico") && (
        <div className="dashboard__admin-panel">
          <h2>Favoritos Urgentes (0 – 7 días)</h2>

          {/* BUSCADOR */}
          <div className="filtros" style={{ marginBottom: "20px" }}>
            <input
              type="text"
              className="form-control busqueda"
              placeholder="Buscar (cliente/expediente/empresa/título)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {Object.entries(filteredFavoritos).map(([userId, favs]) => {
            if (favs.length === 0) return null;
            const clienteNombre = `${favs[0].empresa} (${favs[0].cliente_nombre})`;
            const isExpanded = !!expandedUsers[userId];

            return (
              <div key={userId} className="accordion-item">
                <h3
                  className="accordion-title"
                  onClick={() => toggleUser(userId)}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  {clienteNombre} <span>{isExpanded ? "▲" : "▼"}</span>
                </h3>

                {isExpanded && (
                  <ul>
                    {favs.map((fav) => {
                      const favKey = `${fav.id_usuario}_${fav.id_concurso}`;
                      return (
                        <li key={favKey}>
                          <div className="dashboard__favorite-header">
                            <strong>Concurso:</strong>{" "}
                            <a
                              href={`/concurso/${fav.id_concurso}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {fav.titulo_concurso}
                            </a>{" "}
                            (Exp: {fav.codigo_expediente})
                            <br />
                            <strong>Días restantes:</strong>{" "}
                            <span
                              className={
                                "dashboard__days-remaining " +
                                getDaysRemainingClass(fav.dias_restantes ?? 0)
                              }
                            >
                              {fav.dias_restantes}
                            </span>
                            <br />
                            <strong>Estado Favorito:</strong>{" "}
                            {fav.estado_favorito}
                            <br />
                            {fav.observaciones && (
                              <div className="favorite-observation-display">
                                <em>Observaciones guardadas:</em>{" "}
                                {fav.observaciones}
                              </div>
                            )}
                          </div>

                          <div className="dashboard__favorite-actions">
                            <a
                              href={`/favorito/${fav.id_concurso}/${fav.id_usuario}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Ver Favorito
                            </a>
                            <a
                              href={`/concurso/${fav.id_concurso}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Ver Concurso
                            </a>
                          </div>

                          {fav.soportes?.length ? (
                            <div className="dashboard__favorite-supports">
                              <strong>Soportes pedidos:</strong>
                              <ul>
                                {fav.soportes.map((s) => (
                                  <li key={s.id_soporte}>
                                    {getSoporteName(s.id_tipo_soporte)} –{" "}
                                    <a
                                      href={`${getSoporteUrlPrefix(
                                        s.id_tipo_soporte
                                      )}${fav.id_concurso}/${fav.id_usuario}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Ver Soporte
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <div className="dashboard__favorite-supports">
                              <em>No ha pedido otros soportes</em>
                            </div>
                          )}

                          {(role === "admin" || role === "tecnico") && (
                            <div className="favorite-observations">
                              <textarea
                                className="favorite-observations__textarea"
                                placeholder="Agregar observaciones..."
                                value={
                                  favObservations[favKey] ??
                                  fav.observaciones ??
                                  ""
                                }
                                onChange={(e) =>
                                  setFavObservations((prev) => ({
                                    ...prev,
                                    [favKey]: e.target.value,
                                  }))
                                }
                                rows={4}
                                maxLength={2000}
                              />
                              <div className="favorite-observations__footer">
                                <span className="favorite-observations__counter">
                                  {
                                    (
                                      favObservations[favKey] ??
                                      fav.observaciones ??
                                      ""
                                    ).length
                                  }
                                  /2000
                                </span>
                                <button
                                  className="favorite-observations__button"
                                  onClick={() =>
                                    saveObservations(
                                      fav.id_usuario,
                                      fav.id_concurso,
                                      favKey
                                    )
                                  }
                                >
                                  Guardar Observaciones
                                </button>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}

          {Object.keys(filteredFavoritos).length === 0 && searchTerm && (
            <p className="dashboard__no-data">
              No se encontraron favoritos que coincidan con la búsqueda "
              {searchTerm}".
            </p>
          )}

          {Object.keys(favoritosGrouped).length === 0 && !searchTerm && (
            <p className="dashboard__no-data">
              No hay favoritos urgentes para mostrar.
            </p>
          )}
        </div>
      )}
      <ToastContainer
        position="top-right"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        style={{ marginTop: 125 }}
      />
    </div>
  );
}

export default Dashboard;
